import { apiFetch } from './apiFetch'
import { isDevMock } from '../dev/isDevMock'
import {
  isPrivateHost,
  normalizeUrl,
  type WebsiteFeatures,
} from '../../netlify/functions/lib/detect-website-features'

// Client wrapper around the detect-website-features endpoint (see
// netlify/functions/detect-website-features.ts). The probe MUST run server-side — a browser can't
// read a cross-origin response (CORS) or reach a private host — so this just POSTs the URL and
// returns the server's verdict. In dev-mock mode (no backend) we synthesize a plausible result from
// the URL so the onboarding branches (reverse proxy / login) stay exercisable.

export type { WebsiteFeatures }
export { normalizeUrl }

export async function detectWebsiteFeatures(url: string): Promise<WebsiteFeatures> {
  // Short-circuit obvious non-public hosts (localhost, private-network IPs, single-label names): they
  // can never be reached from Replay QA's browsers, so there's nothing to probe — always a reverse
  // proxy. Skips a pointless round-trip to the detect endpoint (which would reach the same verdict).
  const normalized = normalizeUrl(url)
  let hostname = ''
  try { hostname = new URL(normalized).hostname } catch { /* leave blank → treated as private */ }
  if (!hostname || isPrivateHost(hostname)) {
    return { url: normalized, publiclyAccessible: false, loginProtected: false, loginCertainty: 'none', reason: 'private-host' }
  }

  if (isDevMock()) return mockDetect(url)

  const res = await apiFetch('/api/detect-website-features', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  })
  if (!res.ok) {
    let detail = ''
    try { detail = ((await res.json()) as { error?: string }).error ?? '' } catch { /* non-JSON */ }
    throw new Error(detail || `Could not check the site (${res.status})`)
  }
  return (await res.json()) as WebsiteFeatures
}

// Dev-mock heuristic: private hosts need a reverse proxy; URLs that look auth-y are login protected.
function mockDetect(rawUrl: string): WebsiteFeatures {
  const url = normalizeUrl(rawUrl)
  let hostname = ''
  try { hostname = new URL(url).hostname } catch { /* leave blank */ }
  if (!hostname || isPrivateHost(hostname)) {
    return { url, publiclyAccessible: false, loginProtected: false, loginCertainty: 'none', reason: 'private-host' }
  }
  const loginProtected = /\b(login|signin|sign-in|auth|dashboard|account)\b|^app\./i.test(url) || /^app\./i.test(hostname)
  return { url, publiclyAccessible: true, loginProtected, loginCertainty: loginProtected ? 'confirmed' : 'none', reason: loginProtected ? 'login' : 'ok', statusCode: 200 }
}
